import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from '../ui';
import { surveyService } from '../../services/survey.service';
import { messageHandler } from '../../services/message-handler.service';
import type { SurveyOption, OptionFormData } from '../../types/survey';

interface OptionManagerProps {
  questionId: number;
}

export const OptionManager: React.FC<OptionManagerProps> = ({ questionId }) => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editValue, setEditValue] = useState('');
  const [newText, setNewText] = useState('');
  const [newValue, setNewValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const { data: options = [], isLoading } = useQuery({
    queryKey: ['survey-options', questionId],
    queryFn: () => surveyService.listOptions(questionId),
  });

  const createMutation = useMutation({
    mutationFn: (data: OptionFormData) => surveyService.createOption(questionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-options', questionId] });
      queryClient.invalidateQueries({ queryKey: ['survey-questions'] });
      setNewText('');
      setNewValue('');
      setIsAdding(false);
      messageHandler.success('Opção criada');
    },
    onError: () => messageHandler.error('Erro ao criar opção'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: OptionFormData }) =>
      surveyService.updateOption(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-options', questionId] });
      setEditingId(null);
      messageHandler.success('Opção atualizada');
    },
    onError: () => messageHandler.error('Erro ao atualizar opção'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => surveyService.deleteOption(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-options', questionId] });
      queryClient.invalidateQueries({ queryKey: ['survey-questions'] });
      messageHandler.success('Opção excluída');
    },
    onError: () => messageHandler.error('Erro ao excluir opção'),
  });

  const startEdit = (opt: SurveyOption) => {
    setEditingId(opt.id);
    setEditText(opt.option_text);
    setEditValue(opt.option_value || '');
  };

  const saveEdit = (opt: SurveyOption) => {
    if (!editText.trim()) return;
    updateMutation.mutate({
      id: opt.id,
      data: { option_text: editText.trim(), option_value: editValue.trim() || undefined, order_index: opt.order_index },
    });
  };

  const handleAdd = () => {
    if (!newText.trim()) return;
    createMutation.mutate({
      option_text: newText.trim(),
      option_value: newValue.trim() || undefined,
      order_index: options.length,
    });
  };

  if (isLoading) return <div className="text-sm text-gray-500">Carregando opções...</div>;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Opções de resposta</p>

      {options.map((opt, idx) => (
        <div key={opt.id} className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-5">{idx + 1}.</span>
          {editingId === opt.id ? (
            <>
              <input
                className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Texto da opção"
              />
              <input
                className="w-28 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="Valor (opcional)"
              />
              <button
                onClick={() => saveEdit(opt)}
                className="p-1 text-green-600 hover:text-green-700"
                title="Salvar"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={() => setEditingId(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Cancelar"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{opt.option_text}</span>
              {opt.option_value && (
                <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-1 rounded">
                  {opt.option_value}
                </span>
              )}
              <button
                onClick={() => startEdit(opt)}
                className="p-1 text-gray-400 hover:text-blue-600"
                title="Editar"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                onClick={() => deleteMutation.mutate(opt.id)}
                className="p-1 text-gray-400 hover:text-red-600"
                title="Excluir"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      ))}

      {isAdding ? (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-gray-400 w-5">{options.length + 1}.</span>
          <input
            autoFocus
            className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Texto da opção"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setIsAdding(false); }}
          />
          <input
            className="w-28 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="Valor (opcional)"
          />
          <button
            onClick={handleAdd}
            className="p-1 text-green-600 hover:text-green-700"
            title="Adicionar"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => { setIsAdding(false); setNewText(''); setNewValue(''); }}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Cancelar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => setIsAdding(true)} className="mt-2">
          <Plus className="h-3 w-3 mr-1" />
          Adicionar opção
        </Button>
      )}
    </div>
  );
};
